import { useState, useEffect } from 'react'
import { Menu, X, Phone, Mail, Clock } from 'lucide-react'
// import { LanguageSwitcher } from './LanguageSwitcher'
import { ContactVerification } from './ContactVerification'



interface HeaderProps {
  dictionary: any; // Using any for now to accommodate the expanded dictionary structure
}

export function Header({ dictionary }: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [currentPath, setCurrentPath] = useState("/")

  // Create localized navigation
  const localizedNavigation = [
    { name: dictionary.navigation.home, href: "/", ariaLabel: "Ir a la página de inicio" },
    { name: dictionary.navigation.aboutUs, href: "/quienes-somos", ariaLabel: "Conocer más sobre Neuro Peques" },
    { name: dictionary.navigation.services, href: "/servicios", ariaLabel: "Ver nuestros servicios profesionales" },
    { name: dictionary.navigation.developmentalConditions, href: "/condiciones-del-desarrollo", ariaLabel: "Información sobre condiciones del neurodesarrollo infantil" },
    // TEMPORALMENTE OCULTO - Testimonios (será habilitado cuando tengamos información real)
    // { name: dictionary.navigation.testimonials, href: "/testimonios", ariaLabel: "Leer testimonios de nuestros pacientes" },
    { name: dictionary.navigation.contact, href: "/contacto", ariaLabel: "Contactar a Neuro Peques" },
  ];

  // Efecto para detectar el scroll y cambiar la apariencia del header
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled)
      }
    }

    // Detectar la ruta actual para marcar el enlace activo
    setCurrentPath(window.location.pathname)

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [scrolled])

  return (
    <>
      {/* Top Bar con información de contacto */}
      <div className="hidden md:block bg-neuro-blue text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex space-x-6">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4" aria-hidden="true" />
              <span className="text-sm">{dictionary.header.contactInfo.schedule}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4" aria-hidden="true" />
              <ContactVerification 
                type="phone" 
                value="+50685838056"
                className="text-sm hover:text-neuro-yellow transition-colors cursor-pointer"
              >
                <span aria-label={dictionary.header.contactInfo.phoneLabel}>{dictionary.header.contactInfo.phone}</span>
              </ContactVerification>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4" aria-hidden="true" />
              <ContactVerification 
                type="email" 
                value="info@neuropequescr.com"
                className="text-sm hover:text-neuro-yellow transition-colors cursor-pointer"
              >
                <span aria-label={dictionary.header.contactInfo.emailLabel}>{dictionary.header.contactInfo.email}</span>
              </ContactVerification>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <a 
              href="/contacto" 
              className="bg-neuro-red text-white py-1 px-4 rounded-full text-sm font-medium hover:bg-opacity-90 transition"
              aria-label={dictionary.header.contactInfo.contactPageLabel}
            >
              {dictionary.navigation.contact}
            </a>
            {/* <LanguageSwitcher currentLocale={locale} onLocaleChange={onLocaleChange} /> */}
          </div>
        </div>
      </div>
    
      {/* Header principal */}
      <header 
        className={`sticky top-0 z-50 w-full transition duration-300 ease-in-out ${
          scrolled 
            ? "bg-white shadow-md py-2" 
            : "bg-white/95 backdrop-blur-sm py-4"
        }`}
        role="banner"
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo principal con datos estructurados para SEO */}
            <a 
              href="/" 
              className="flex items-center"
              aria-label={dictionary.header.logo.label}
            >
              <img
                src="/images/logo-neuropeques-optimized.svg"
                alt={dictionary.header.logo.alt}
                width={180}
                height={60}
                className="h-auto"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                itemProp="logo"
              />
            </a>
            
            {/* Navegación Desktop */}
            <nav className="hidden md:flex space-x-1" aria-label={dictionary.header.navigation.label}>
              {localizedNavigation.map((item) => {
                const isActive = currentPath === item.href || currentPath.startsWith(`${item.href}/`);
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    className={`text-neuro-dark-blue relative hover:text-neuro-blue font-medium px-3 py-2 rounded-md transition-colors ${
                      isActive ? 'font-bold' : ''
                    }`}
                    aria-label={item.ariaLabel}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.name}
                    {isActive && (
                      <span
                        className={`absolute bottom-0 left-0 w-full h-0.5 bg-neuro-green ${
                          isActive ? 'w-full' : 'w-1/2'
                        }`}
                      />
                    )}
                  </a>
                );
              })}
            </nav>
            
            {/* Language switcher for smaller screens */}
            {/* <div className="md:hidden flex items-center mr-2">
              <LanguageSwitcher currentLocale={locale} onLocaleChange={onLocaleChange} />
            </div> */}
            
            {/* Botón de menú móvil */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-neuro-dark-blue hover:text-neuro-blue focus:outline-none"
                aria-expanded={isOpen}
                aria-controls="mobile-menu"
                aria-label={isOpen ? "Cerrar menú principal" : "Abrir menú principal"}
              >
                <span className="sr-only">{isOpen ? "Cerrar menú principal" : "Abrir menú principal"}</span>
                {isOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Menú móvil */}
        <div
          id="mobile-menu"
          className={`md:hidden ${
            isOpen ? "block" : "hidden"
          } bg-white border-t border-gray-200`}
          aria-label="Menú móvil"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {localizedNavigation.map((item) => {
              const isActive = currentPath === item.href || currentPath.startsWith(`${item.href}/`);
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className={`text-neuro-dark-blue hover:bg-neuro-green/20 block px-3 py-2 rounded-md text-base font-medium ${
                    isActive ? 'bg-neuro-green/10 font-bold' : ''
                  }`}
                  onClick={() => setIsOpen(false)}
                  aria-label={item.ariaLabel}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.name}
                </a>
              );
            })}
            <div className="mt-4 px-3 py-2">
              <a 
                href="/contacto" 
                className="block w-full bg-neuro-red text-white py-2 px-4 rounded-full text-center font-medium hover:bg-opacity-90 transition"
                onClick={() => setIsOpen(false)}
                aria-label="Ir a la página de contacto para agendar una cita"
              >
                {dictionary.common.scheduleAppointment}
              </a>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}
